import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { TableHeader, TableRow } from "../../components/TableComponents/TableComponents";
import { headersType } from "../../models";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useDispatch } from "react-redux";
import { User } from "../../models/user.model";
import { getPatientPlanInvoice } from "../../redux/slices/PatientSlice";
import moment from "moment";
import PlanReceiptPdf, { PlanReceiptData } from "../../components/forms/Receipt/PlanReceiptPdf";
const html2pdf = require("html2pdf.js");
export const headers: headersType[] = [
  {
    title: "Plan Name",
    extraClasses: "w-full hidden pl-2 lg:block truncate",
    sortKey: "planName",
    isClickable: false,
  },
  {
    title: "Annual Plan",
    extraClasses: "w-full lg:w-240 pl-0",
    sortKey: "planInterval",
    isClickable: false,
  },
  {
    title: "Plan Status",
    extraClasses: "w-160 hidden lg:block  ",
    sortKey: "planState",
    isClickable: false,
  },
  {
    title: "Date Created",
    extraClasses: "w-160 hidden lg:block  ",
    sortKey: "planStartDate",
    isClickable: false,
  },
  {
    title: "",
    extraClasses: "w-56 hidden lg:block  ",
    sortKey: "",
    isClickable: false,
  },
];

const PatientPlanReceipts = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  //const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [extraClasses] = useState(headers.map(head => head.extraClasses));
  const [sortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex] = useState<number>(-1);
  const [data, setData] = useState<any[]>([]);
  const [receiptData, setReceiptData] = useState<PlanReceiptData | undefined>(undefined);
  const [downloading, setDownloading] = useState(false);

  const handleSort = (index: number) => {};

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const init = async (currentUser: User, page = 0, size = 10) => {
    if (currentUser && currentUser?.offices && currentUser?.offices?.length && currentUser?.offices[0]?.officeId) {
      if (currentUser?.userAnnualPlan) {
        const AnnualId = currentUser?.userAnnualPlan?.userAnnualPlanId;
        const listingInvoiceData = await dispatch(
          getPatientPlanInvoice({
            officeId: currentUser.offices[0].officeId,
            userId: currentUser.userId,
            userAnnualPlanId: AnnualId,
            page,
            size,
          })
        );
        const payload = listingInvoiceData?.payload as any;
        const rows = Array.isArray(payload) ? payload : payload?.content ?? payload?.data ?? [];
        setData(Array.isArray(rows) ? rows : []);
      }
    }
  };

  useEffect(() => {
    if (currentUser) init(currentUser);
  }, [currentUser]);

  // Generate and download PDF when receiptData is set
  useEffect(() => {
    const generateAndDownload = async () => {
      if (!receiptData) return;
      setDownloading(true);
      const element = document.getElementById("plan-receipt-container-pdf-list");
      if (!element) {
        setDownloading(false);
        return;
      }
      const wasHidden = element.classList.contains("hidden");
      if (wasHidden) element.classList.remove("hidden");
      await new Promise(requestAnimationFrame);
      const opt = {
        margin: [5, 0, 5, 0],
        filename: `DocnoteReceipt-${receiptData.invoiceNumber}`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 4, dpi: 192, letterRendering: true, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", compress: true, orientation: "portrait" },
        pagebreak: { mode: "avoid-all" },
      } as any;
      try {
        await html2pdf().set(opt).from(element).save();
      } catch (e) {
      } finally {
        if (wasHidden) element.classList.add("hidden");
        setDownloading(false);
        setReceiptData(undefined);
      }
    };
    generateAndDownload();
  }, [receiptData]);

  return (
    <>
      <main>
        <div className=" mx-auto min-h-[80vh] mt-4">
          {data.length ? (
            <div className="block">
              <p className="text-base mb-5 font-medium">You will find all your current and past receipts below for Annual Plan.</p>

              <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                {headers.map((head, index) => (
                  <TableHeader
                    title={head.title}
                    index={index}
                    key={index}
                    sortIcon={sortIcons[index]}
                    handleSort={handleSort}
                    extraClassesHeader={extraClasses[index]}
                    isClickable={head.isClickable}
                  />
                ))}
              </div>

              {data?.map((receipt, index) => {
                const sortedData = {
                  planName: receipt?.officeDetails?.planName ?? "-",
                  planInterval: receipt?.officeDetails?.planInterval ?? "-",
                  planState: receipt?.status,
                  date: moment(receipt?.dateOfIssue ?? receipt?.dateOfIssue ?? "").format("ll"),
                  comment: (
                    <div className="flex gap-2 cursor-pointer" onClick={() => setReceiptData(receipt as PlanReceiptData)}>
                      <FileDownloadOutlinedIcon className="text-docsigna-orange" />
                    </div>
                  ),
                };
                return (
                  <TableRow
                    data={Object.values(sortedData)}
                    key={`${index}-${receipt?.fileDto?.fileId ?? index}`}
                    handleCloseTooltip={() => {}}
                    handleOpenTooltip={() => {}}
                    tooltipVisible={false}
                    extraClassesRow={extraClasses}
                    onRowClick={() => undefined}
                  />
                );
              })}
            </div>
          ) : (
            <p className="font-semibold">You have no receipts at this time</p>
          )}
        </div>
      </main>
      {/* Hidden PDF container for receipt generation from list */}
      <div className="absolute -top-[1000%]">
        <div id="plan-receipt-container-pdf-list" className="hidden">
          {receiptData ? <PlanReceiptPdf data={receiptData} /> : null}
        </div>
      </div>
    </>
  );
};

export default PatientPlanReceipts;
